<template>
  <div class="bg-white rounded-lg p-6 mb-6">
    <!-- Neighborhood Map -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <h2 class="text-xl font-bold text-gray-900">{{ t('property.neighborhoodMap') }}</h2>
        <Tooltip position="top">
          <template #trigger>
            <HelpCircle :size="16" class="text-gray-500 cursor-pointer" />
          </template>
          <template #content>
            <p>{{ t('property.copyright.mlit', { url: mlitDataSourceUrls.urbanPlans }) }}</p>
            <p>{{ t('property.copyright.disaportal') }}</p>
          </template>
        </Tooltip>
      </div>
      <a
        v-if="deal.building.loc"
        :href="googleMapsUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1 text-sm text-gray-500 hover:text-brand transition-colors"
      >
        {{ t('property.viewOnGoogleMaps') }}
        <ExternalLink :size="14" />
      </a>
    </div>
    <div class="flex flex-wrap gap-2 mb-4">
      <label
        class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
        :class="showStations ? 'bg-brand hover:bg-brand/90 text-white' : 'hover:bg-gray-200'"
      >
        <Train :size="16" />
        <span>{{ t('propertyCard.station') }}</span>
        <input v-model="showStations" type="checkbox" class="hidden" />
      </label>
      <label
        class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
        :class="showBusStops ? 'bg-brand hover:bg-brand/90 text-white' : 'hover:bg-gray-200'"
      >
        <BusIcon :size="16" />
        <span>{{ t('sections.busStops') }}</span>
        <input v-model="showBusStops" type="checkbox" class="hidden" />
      </label>
      <label
        class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
        :class="showSchools ? 'bg-brand hover:bg-brand/90 text-white' : 'hover:bg-gray-200'"
      >
        <SchoolIcon :size="16" />
        <span>{{ t('property.schools') }}</span>
        <input v-model="showSchools" type="checkbox" class="hidden" />
      </label>
      <div class="relative">
        <button
          class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
          :class="showHazardMapPanel || selectedHazardMapTypes.length ? 'bg-brand hover:bg-brand/90 text-white' : 'hover:bg-gray-200'"
          @click="showHazardMapPanel = !showHazardMapPanel"
        >
          <Layers :size="16" />
          <span>{{ t('searchResults.hazardMaps') }}</span>
        </button>
        <div
          v-if="showHazardMapPanel"
          class="absolute top-full mt-2 right-0 md:left-0 bg-white rounded-lg shadow-lg p-4 z-[1000]
                  w-64 md:w-max"
        >
          <h4 class="font-semibold text-gray-700 mb-2">{{ t('searchResults.selectHazardLayers') }}</h4>
          <div class="space-y-2">
            <label
              v-for="(map, id) in hazardMapTypes"
                :label="t(`hazardMap.${id}.name`) + (map.sub ? ` (${t(`hazardMap.${id}.sub`)})` : '')"
              :key="id"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                v-model="selectedHazardMapTypes"
                type="checkbox"
                :value="id"
              />
              <div class="">
                <span>{{ t(`hazardMap.${id}.name`) }}</span>
                <span v-if="map.sub" class="text-xs text-gray-400 ml-2">{{ t(`hazardMap.${id}.sub`) }}</span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div ref="mapWrapper" class="h-[500px] w-full rounded-lg overflow-hidden relative">
      <div v-if="!isMapVisible" class="h-full w-full flex items-center justify-center bg-gray-100">
        <MapPin :size="32" class="text-gray-300" />
      </div>
      <LMap
        v-else
        ref="map"
        :zoom="mapDefaults.zoom"
        :center="[mapDefaults.center.lat, mapDefaults.center.lng]"
        :use-global-leaflet="false"
        style="width: 100%; height: 100%"
        @ready="onReady"
        @moveend="onMoveEnd"
      >
        <LTileLayer
          v-if="baseLayerTier === 'gsi'"
          :url="gsiBaseTileLayer.url"
          :attribution="gsiBaseTileLayer.attribution"
          :min-zoom="gsiBaseTileLayer.minZoom"
          :max-zoom="gsiBaseTileLayer.maxZoom"
          layer-type="base"
        />
        <LTileLayer
          v-else-if="baseLayerTier === 'osm'"
          :url="osmTileUrl"
          :attribution="osmBaseTileLayer.attribution"
          :min-zoom="osmBaseTileLayer.minZoom"
          :max-zoom="osmBaseTileLayer.maxZoom"
          layer-type="base"
          @tileerror="onOsmTileError"
        />
        <LTileLayer
          v-for="id in selectedHazardMapTypes"
          :key="`hazard-${id}`"
          :url="hazardMapTypes[id].urlPattern"
          :opacity="hazardMapTypes[id].opacity ?? 0.5"
          :min-zoom="hazardMapTypes[id].minZoom"
          :max-zoom="hazardMapTypes[id].maxZoom"
          pane="overlayPane"
        />

        <template v-if="blankIcon">
          <LMarker
            v-for="(school, index) in filteredSchools"
            :key="`school-${index}`"
            :lat-lng="[school.geometry.coordinates[1], school.geometry.coordinates[0]]"
            :icon="blankIcon"
          >
            <LTooltip :options="markerTooltipOptions">
              <a :href="getGoogleMapsSearchUrlByName(school.P29_004)" target="_blank" rel="noopener noreferrer">
                <div class="relative group cursor-pointer flex justify-center">
                  <div class="p-2 bg-indigo-800 rounded-full shadow-md group-hover:scale-110 transition-transform z-10">
                    <SchoolIcon :size="20" class="text-white" />
                  </div>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white text-gray-800 text-sm rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100 origin-bottom">
                    {{ school.P29_004 }}
                  </div>
                </div>
              </a>
            </LTooltip>
          </LMarker>
          <LMarker
            v-for="(busStop, index) in filteredBusStops"
            :key="`bus-${index}`"
            :lat-lng="[busStop.geometry.coordinates[1], busStop.geometry.coordinates[0]]"
            :icon="blankIcon"
          >
            <LTooltip :options="markerTooltipOptions">
              <a :href="getGoogleMapsSearchUrlByName(busStop.P11_001)" target="_blank" rel="noopener noreferrer">
                <div class="relative group cursor-pointer flex justify-center">
                  <div class="p-2 bg-green-800 rounded-full shadow-md group-hover:scale-110 transition-transform z-10">
                    <BusIcon :size="20" class="text-white" />
                  </div>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white text-gray-800 text-sm rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100 origin-bottom">
                    {{ busStop.P11_001 }}
                  </div>
                </div>
              </a>
            </LTooltip>
          </LMarker>
          <LMarker
            v-for="(station, index) in filteredStations"
            :key="`station-${index}`"
            :lat-lng="[station.geometry.coordinates[1], station.geometry.coordinates[0]]"
            :icon="blankIcon"
          >
            <LTooltip :options="markerTooltipOptions">
              <a :href="getGoogleMapsSearchUrlByName(station.label)" target="_blank" rel="noopener noreferrer">
                <div class="relative group cursor-pointer flex justify-center">
                  <div class="p-2 bg-blue-500 rounded-full shadow-md group-hover:scale-110 transition-transform z-10">
                    <Train :size="20" class="text-white" />
                  </div>
                  <div
                    class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white text-gray-800 text-sm rounded-lg shadow-lg whitespace-nowrap
                              opacity-0 group-hover:opacity-100 transition-all duration-300
                              transform scale-90 group-hover:scale-100 origin-bottom"
                  >
                    {{ station.label }}
                  </div>
                </div>
              </a>
            </LTooltip>
          </LMarker>
          <LMarker
            v-for="(plan, index) in urbanPlansPoints"
            :key="`urban-plan-point-${index}`"
            :lat-lng="[plan.geometry.coordinates[1], plan.geometry.coordinates[0]]"
            :icon="blankIcon"
          >
            <LTooltip :options="markerTooltipOptions">
              <a :href="getGoogleMapsSearchUrlByName(plan.YoutoName || plan.TokeiName)" target="_blank" rel="noopener noreferrer">
                <div class="relative group cursor-pointer flex justify-center">
                  <div class="p-2 bg-white rounded-full shadow-md group-hover:scale-110 transition-transform z-10">
                    <Building2 :size="20" class="text-yellow-600" />
                  </div>
                  <div
                    class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white text-gray-800 text-sm rounded-lg shadow-lg whitespace-nowrap
                              opacity-0 group-hover:opacity-100 transition-all duration-300
                              transform scale-90 group-hover:scale-100 origin-bottom"
                  >
                    {{ plan.YoutoName || plan.TokeiName }}
                  </div>
                </div>
              </a>
            </LTooltip>
          </LMarker>
          <LMarker
            v-if="deal.building.loc"
            :lat-lng="[deal.building.loc.coordinates[1], deal.building.loc.coordinates[0]]"
            :icon="blankIcon"
          >
            <LTooltip :options="markerTooltipOptions">
              <div class="relative">
                <div
                  :class="[
                    'px-3 py-1.5 rounded-lg shadow-lg transition-all',
                    activeMarker === deal.id
                      ? 'bg-brand text-white scale-110 z-10'
                      : 'bg-white text-gray-900 hover:bg-brand hover:text-white',
                  ]"
                >
                  <div class="text-xs font-bold whitespace-nowrap">
                    {{ deal.price ? formatPrice(deal.price) : formatPrice(deal.rent) }}
                  </div>
                </div>
                <MapPin
                  :size="20"
                  :class="[
                    'absolute left-1/2 -translate-x-1/2 -bottom-4 transition-colors',
                    activeMarker === deal.id ? 'text-brand' : 'text-gray-600',
                  ]"
                  fill="currentColor"
                />
              </div>
            </LTooltip>
          </LMarker>
        </template>
      </LMap>
      <div
        v-if="isMapVisible && selectedHazardMapTypes.length"
        class="legend absolute left-2.5 bottom-7 z-[1000]"
      >
        <img
          class="!max-w-30 md:!max-w-50 bg-white"
          :src="hazardMapTypes[selectedHazardMapTypes[selectedHazardMapTypes.length - 1]].legendUrl"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css';
import { ref, computed, watch, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import type L from 'leaflet';
import type { PageContext } from 'vike/types'
import { usePageContext } from "vike-vue/usePageContext";
import { useI18n } from 'vue-i18n';
import {
  Layers, Train, Building2, MapPin,
  School as SchoolIcon, BusFront as BusIcon, HelpCircle, ExternalLink,
} from 'lucide-vue-next';
import {
  useAddress,
  useCurrency,
  useMap,
} from '@repo/ui';
import Tooltip from '@repo/ui/components/Tooltip.vue';
import { LMap, LTileLayer, LMarker, LTooltip } from '@vue-leaflet/vue-leaflet';
import { types as hazardMapTypes } from '@repo/modules/hazardMap';
import { gsiBaseTileLayer, osmBaseTileLayer } from '@repo/ui/utils/map';
import { Neighborhood } from '@repo/types';
import { PageDataBase } from '@repo/ui/hooks';

const pageContext = usePageContext() as PageContext<PageDataBase>;
const { t, locale } = useI18n({});

const neighborhood = ref<Neighborhood | undefined>(pageContext.data.neighborhood);

const { format: formatPrice } = useCurrency(pageContext.data.currencies);

const { deal } = useAddress();

const dealPoint = computed(() => {
  return deal.value?.building.loc ?
    { lat: deal.value.building.loc.coordinates[1], lng: deal.value.building.loc.coordinates[0] } :
    { lat: 0, lng: 0 }
})

const googleMapsUrl = computed(() => {
  return `https://www.google.com/maps/search/?api=1&query=${dealPoint.value.lat},${dealPoint.value.lng}`;
})

const mapRef = useTemplateRef('map');
const {
  mapDefaults,
  baseLayerTier,
  osmTileUrl,
  onOsmTileError,
  onReady,
  updateBounds,
} = useMap({ center: dealPoint.value, zoom: 16 })

function onMoveEnd() {
  const leafletMap = mapRef.value?.leafletObject;
  if (leafletMap) updateBounds(leafletMap);
}

watch(deal, () => {
  const leafletMap = mapRef.value?.leafletObject;
  if (leafletMap) {
    leafletMap.setView([dealPoint.value.lat, dealPoint.value.lng]);
  }
}, { immediate: true })

const blankIcon = ref<L.DivIcon | null>(null);
onMounted(async () => {
  const L = await import('leaflet');
  blankIcon.value = L.divIcon({ className: 'leaflet-blank-icon', iconSize: [0, 0] });
});

// Defer mounting LMap (and its tile requests) until the map section scrolls near the
// viewport, since it sits well below the fold and many visitors never reach it.
const isMapVisible = ref(false);
const mapWrapperRef = useTemplateRef('mapWrapper');
let mapVisibilityObserver: IntersectionObserver | null = null;

onMounted(() => {
  if (!mapWrapperRef.value) return;
  mapVisibilityObserver = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting) {
      isMapVisible.value = true;
      mapVisibilityObserver?.disconnect();
    }
  }, { rootMargin: '200px' });
  mapVisibilityObserver.observe(mapWrapperRef.value);
});

onBeforeUnmount(() => {
  mapVisibilityObserver?.disconnect();
});

const markerTooltipOptions = {
  permanent: true,
  direction: 'center' as const,
  interactive: true,
  className: 'leaflet-marker-tooltip',
};

const showSchools = ref(true);
const showBusStops = ref(false);
const showStations = ref(true);
const showUrbanPlans = ref(false);

const showHazardMapPanel = ref(false);
const selectedHazardMapTypes = ref<string[]>([]);

const filteredSchools = computed(() => {
  return showSchools.value ? (neighborhood.value?.schools ?? []).filter(school => school.geometry.type === 'Point') : [];
});

const filteredBusStops = computed(() => {
  return showBusStops.value ? (neighborhood.value?.busStops ?? []).filter(busStop => busStop.geometry.type === 'Point') : [];
});

function averageCoordinates(points: Array<[number, number]>): [number, number] {
  return [
    points.map(coords => coords[0]).reduce((a, b) => a + b, 0) / points.length,
    points.map(coords => coords[1]).reduce((a, b) => a + b, 0) / points.length,
  ]
}

const filteredStations = computed(() => {
  return showStations.value ?
    (neighborhood.value?.stations ?? []).filter(station => station.geometry.type === 'LineString')
      .map(station => ({
        ...station,
        label: station.station?.labels[locale.value] ?? station.N02_004,
        geometry: { type: 'Point', coordinates: averageCoordinates(station.geometry.coordinates as [number, number][]) }
      }))
    : [];
});

const urbanPlansPoints = computed(() => {
  return showUrbanPlans.value ? (neighborhood.value?.urbanPlans ?? []).filter(plan => plan.geometry.type === 'Point') : [];
});

function getGoogleMapsSearchUrlByName(name: string) {
  if (!name) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
}

const activeMarker = ref<number | null>(null);

const mlitDataSourceUrls = {
  stations: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N02-v3_1.html',
  schools: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P29-v2_0.html',
  elementarySchoolZones: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A27-v3_0.html',
  juniorHighSchoolZones: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A32-v3_0.html',
  urbanPlans: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2024.html',
  landPriceHistory: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-v3_1.html',
};
</script>

<style scoped>
:global(.leaflet-marker-tooltip) {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
}
:global(.leaflet-marker-tooltip::before) {
  display: none;
}
</style>
